/**
 * Login Page — Industry design system ("Login" artboard).
 *
 * Light technical ground, hairline top nav with the bordered "Z" plate +
 * Barlow Condensed wordmark, a 2-column hero (kicker, 50px H1, blueprint
 * dispatch-rack demo card, hero stats) and a 420px blueprint form card
 * with uppercase mini-labels over square hairline inputs.
 *
 * Behavior contracts kept intact:
 *   - input[name="username"] / input[name="password"] selectors
 *   - "Entrar" submit, MFA second stage, passkey path
 *   - "Recuperar minha senha" / "Receber link de redefinição" recovery CTAs
 *   - responsive phone/tablet login using the same authenticated flow
 *   - AccessSupportPanel for delivery-hook failures
 */

import { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Link } from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/auth';
import { resolvePostLoginPath } from '../services/postLoginDestination';
import AccessSupportPanel from '../components/common/AccessSupportPanel';

const IS_DEV = Boolean(import.meta?.env?.DEV);

const HEADING = 'var(--font-heading)';
const INK = 'var(--color-neutral-900)';
const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const ACC8 = 'var(--color-accent-800)';
const MUTED58 = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const MUTED68 = 'color-mix(in srgb, var(--color-text) 68%, transparent)';
const TINT = 'color-mix(in srgb, var(--color-accent) 8%, transparent)';

const labelSx = {
  display: 'block',
  fontFamily: HEADING,
  fontSize: '9.5px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: MUTED58,
  mb: '4px',
};

const inputStyle = {
  width: '100%',
  minHeight: 40,
  fontSize: 14.5,
  background: 'transparent',
  border: '1px solid var(--color-divider)',
  borderRadius: 0,
  padding: '0 11px',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-body)',
  color: 'var(--color-text)',
};

const upLinkSx = {
  fontFamily: HEADING,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: ACC7,
  textDecoration: 'none',
  '&:hover': { color: ACC8 },
};

const primaryBtnSx = {
  width: '100%',
  minHeight: 44,
  border: `1px solid ${ACC}`,
  background: ACC,
  color: 'var(--color-bg)',
  fontFamily: HEADING,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderRadius: 0,
  cursor: 'pointer',
  '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
  '&:active': { background: ACC7 },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

const secondaryBtnSx = {
  fontFamily: HEADING,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  minHeight: 36,
  padding: '0 14px',
  cursor: 'pointer',
  border: '1px solid var(--color-divider)',
  background: 'transparent',
  color: 'var(--color-text)',
  borderRadius: 0,
  '&:hover': { borderColor: 'rgba(29,31,32,0.45)' },
};

const errorBoxSx = {
  p: '9px 11px',
  border: `1px solid ${INK}`,
  fontSize: 12.5,
  lineHeight: 1.45,
  color: INK,
};

const successBoxSx = {
  p: '11px 13px',
  border: `1px solid ${ACC}`,
  background: 'color-mix(in srgb, var(--color-accent) 7%, transparent)',
  fontSize: 13,
  lineHeight: 1.55,
  color: ACC8,
};

function ZPlateBrand({ size = 28, fontSize = 14, wordSize = 18 }) {
  return (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
      <Box
        component="span"
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: size,
          height: size,
          border: '1.5px solid var(--color-text)',
          fontFamily: HEADING,
          fontSize,
          fontWeight: 600,
          color: 'var(--color-text)',
        }}
      >
        Z
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: HEADING,
          fontSize: wordSize,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--color-text)',
        }}
      >
        Zulbot
      </Box>
    </Box>
  );
}

const DEMO_ROWS = [
  { t: 'NOTAMs relidos após o planejamento', meta: 'AIS-WEB · 10:42', ok: true },
  { t: 'METAR / TAF revisados na última hora', meta: 'REDEMET · 10:38', ok: true },
  { t: "Combustível planejado A+B+C+45'", meta: 'crítico · aberto', ok: false },
  { t: 'Performance do dia recalculada', meta: 'AFM · 10:20', ok: true },
  { t: 'Slot CGNA confirmado', meta: 'crítico · aberto', ok: false },
];

const HERO_STATS = [
  { v: '9 min', k: 'para despachar um trecho' },
  { v: '82%', k: 'de eFRAT por trecho na base' },
  { v: '0', k: 'planilha paralela' },
];

/**
 * True when the login request never reached a backend response — i.e. axios
 * threw with no `.response` attached (timeout, ECONNABORTED, ERR_NETWORK,
 * gateway-level timeout). Used to surface a friendlier copy that distinguishes
 * "the backend is warming up" from "your password is wrong".
 */
function _isLoginTimeoutOrNetworkError(err) {
  if (!err || err.response) return false;
  const code = String(err.code || '').toUpperCase();
  const message = String(err.message || '').toLowerCase();
  return (
    code === 'ECONNABORTED'
    || code === 'ERR_NETWORK'
    || code === 'ETIMEDOUT'
    || message.includes('timeout')
    || message.includes('network error')
  );
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, completeMfaLogin } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [errorIsAuth, setErrorIsAuth] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);
  // MFA second step: set when /auth/login/ returns an mfa_required envelope.
  const [mfaState, setMfaState] = useState(null); // { mfaToken, methods, enrollmentRequired }
  const [mfaCode, setMfaCode] = useState('');
  const [mfaUseRecovery, setMfaUseRecovery] = useState(false);

  // Sticky flag: once we know this user just finished the activation
  // flow, we keep guiding them toward password recovery on the FIRST
  // failed login. Without this, a 401 after activation just shows
  // "Invalid username or password" with no path forward, which was
  // the exact dead-end reported by ricaxixo/admin123 and
  // fogaca@flyppjcc.com.
  const [justActivated, setJustActivated] = useState(false);
  const [recoveryEmailHint, setRecoveryEmailHint] = useState('');

  useEffect(() => {
    const email = (searchParams.get('email') || '').trim();
    const username = (searchParams.get('username') || '').trim();
    const accountCreated = searchParams.get('account_created') === '1';

    setFormData((current) => {
      if (current.username) return current;
      return {
        ...current,
        username: accountCreated ? (email || username) : (username || email),
      };
    });

    if (accountCreated) {
      setJustActivated(true);
      setRecoveryEmailHint(email || (username.includes('@') ? username : ''));
      setInfoMessage('Conta criada com sucesso. Faça seu primeiro login para acessar sua nova empresa no Zulbot.');
    } else {
      setInfoMessage('');
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setErrorIsAuth(false);
  };

  const finishLogin = async () => {
    const redirectParam = String(searchParams.get('redirect') || '').trim();
    const stateFrom = typeof location.state?.from?.pathname === 'string'
      ? `${location.state.from.pathname || ''}${location.state.from.search || ''}${location.state.from.hash || ''}`
      : '';
    const safeRedirect = [redirectParam, stateFrom]
      .find((candidate) => (
        candidate
        && candidate.startsWith('/')
        && !candidate.startsWith('//')
        && !candidate.startsWith('/login')
      ));
    // Honor an explicit return-to/redirect target when present. Otherwise
    // pick the default landing: brand-new / empty operations are guided to
    // "Primeiros Passos" so they can register the minimum data, while any
    // operation with real data lands on Mission Control (the default page
    // after login). The resolver never throws — it falls back to Mission
    // Control if the onboarding status can't be read in time.
    const destination = safeRedirect || (await resolvePostLoginPath());
    navigate(destination, { replace: true });
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaState?.mfaToken) return;
    setError('');
    setErrorIsAuth(false);
    setLoading(true);
    try {
      await completeMfaLogin({
        mfaToken: mfaState.mfaToken,
        method: mfaUseRecovery ? 'recovery' : 'totp',
        code: mfaCode.trim(),
      });
      await finishLogin();
    } catch (err) {
      const status = err?.response?.status;
      setErrorIsAuth(status === 401 || status === 400);
      setError(
        err?.response?.data?.detail ||
          'Código inválido. Verifique e tente novamente.'
      );
      setLoading(false);
    }
  };

  const handlePasskeyVerify = async () => {
    if (!mfaState?.mfaToken) return;
    setError('');
    setLoading(true);
    try {
      const mfaService = (await import('../services/mfa')).default;
      await mfaService.verifyLoginPasskey(mfaState.mfaToken);
      await finishLogin();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'Não foi possível verificar a passkey. Tente o código do app.'
      );
      setLoading(false);
    }
  };

  const cancelMfa = () => {
    setMfaState(null);
    setMfaCode('');
    setMfaUseRecovery(false);
    setError('');
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorIsAuth(false);
    setLoading(true);

    try {
      const tenantSchema = (
        searchParams.get('tenant_schema') ||
        authService.getLastTenantSchema() ||
        ''
      ).trim();
      const result = await login(formData.username, formData.password, tenantSchema);
      // Two-step login: the password was correct but a second factor is
      // required. Show the verification panel instead of navigating.
      if (result && result.mfaRequired) {
        setMfaState({
          mfaToken: result.mfaToken,
          methods: result.methods || [],
          enrollmentRequired: Boolean(result.enrollmentRequired),
        });
        setLoading(false);
        return;
      }
      await finishLogin();
    } catch (err) {
      const status = err?.response?.status;
      const isAuthFailure = status === 401 || status === 400;
      setErrorIsAuth(isAuthFailure);
      if (isAuthFailure) {
        setError(
          err.response?.data?.detail ||
            'Não conseguimos entrar com essas credenciais. Confira o usuário e a senha.'
        );
      } else if (_isLoginTimeoutOrNetworkError(err)) {
        // When the SPA's axios timeout fires or the connection is reset,
        // there's no HTTP response at all (status is undefined). Surfacing
        // the generic "Erro ao tentar entrar" message left users guessing
        // whether their credentials were wrong (they weren't — the backend
        // was warming up after a rolling deploy). Be explicit so the user
        // knows to wait a moment and retry instead of resetting a password
        // that already works. See the 2026-05-22 incident in
        // DEPLOYMENT_LOG.md.
        setError(
          err.response?.data?.detail ||
            'O servidor está respondendo devagar agora — aguarde alguns segundos e tente entrar novamente. Seu usuário e senha continuam válidos.'
        );
      } else {
        setError(
          err.response?.data?.detail ||
            'Erro ao tentar entrar. Tente novamente em instantes.'
        );
      }
      // Stash the email hint for the recovery CTA: if the user typed an
      // email-like identifier, that's the strongest signal for which
      // address should receive the reset link.
      if (formData.username.includes('@')) {
        setRecoveryEmailHint(formData.username.trim());
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Build the /forgot-password URL with the best available email hint.
   * Priority: the email the user just typed (if it looks like one) ->
   * the email passed in via ?email= -> the username if it contains '@'.
   */
  const buildRecoveryHref = () => {
    const candidate = (recoveryEmailHint || formData.username || '').trim();
    const looksLikeEmail = candidate.includes('@') && candidate.includes('.');
    if (!looksLikeEmail) return '/forgot-password';
    const params = new URLSearchParams({ email: candidate });
    return `/forgot-password?${params.toString()}`;
  };

  return (
    <Box
      className="zb-industry"
      sx={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Top hairline nav: Z plate + wordmark + right kicker ── */}
      <Box
        component="nav"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 4,
          py: '14px',
          borderBottom: '1px solid var(--color-divider)',
          flexWrap: 'wrap',
        }}
      >
        <ZPlateBrand />
        <Box sx={{ flex: 1 }} />
        <Box
          component="span"
          sx={{
            fontFamily: HEADING,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'color-mix(in srgb, var(--color-text) 55%, transparent)',
          }}
        >
          Acesso à operação
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          flex: 1,
          maxWidth: 1280,
          width: '100%',
          mx: 'auto',
          px: 4,
          pt: '44px',
          pb: 5,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.1fr) minmax(0, 0.95fr)' },
          gap: '36px',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
      >
        {/* ── Left: hero + blueprint dispatch rack + stats ── */}
        <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' } }}>
          <Box className="ind-kicker ind-kicker--accent" sx={{ fontSize: '11px' }}>
            De piloto para piloto
          </Box>
          <Typography
            component="h1"
            sx={{
              m: '10px 0 0',
              fontFamily: HEADING,
              fontSize: 50,
              fontWeight: 600,
              lineHeight: 1.03,
              letterSpacing: '-0.03em',
              maxWidth: '24ch',
            }}
          >
            A operação inteira decidida antes de decolar
          </Typography>
          <Typography sx={{ m: '15px 0 0', fontSize: 15.5, lineHeight: 1.6, maxWidth: '54ch', color: 'color-mix(in srgb, var(--color-text) 76%, transparent)' }}>
            Checklist operacional, eFRAT, diário de bordo, combustível e custos no mesmo lugar — com o piloto operando pelo WhatsApp e o despacho vendo tudo no rack.
          </Typography>

          <Box className="blueprint" sx={{ mt: 3, p: 0, background: 'transparent', boxShadow: 'var(--ind-shadow-md)', maxWidth: 560 }}>
            <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '11px 14px', borderBottom: '1px dashed var(--color-neutral-300)' }}>
              <Box component="span" sx={{ fontFamily: HEADING, fontSize: 10, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'color-mix(in srgb, var(--color-text) 60%, transparent)' }}>
                Rack de despacho · SBSP → SBRJ · ETD 09:30
              </Box>
              <Box sx={{ flex: 1 }} />
              <Box component="span" sx={{ fontFamily: HEADING, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', p: '2px 7px', border: `1px solid ${INK}`, color: INK }}>
                NO-GO
              </Box>
            </Box>
            {DEMO_ROWS.map((r) => (
              <Box
                key={r.t}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '11px',
                  p: '9px 14px',
                  borderBottom: '1px dashed var(--color-neutral-200)',
                  background: r.ok ? 'transparent' : 'color-mix(in srgb, var(--color-text) 4%, transparent)',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    width: 26,
                    height: 22,
                    fontFamily: HEADING,
                    fontSize: 12,
                    fontWeight: 600,
                    border: `1.5px solid ${r.ok ? ACC : INK}`,
                    background: r.ok ? TINT : 'transparent',
                    color: r.ok ? ACC7 : INK,
                    transform: r.ok ? 'rotate(-9deg)' : 'none',
                  }}
                >
                  {r.ok ? '✓' : '!'}
                </Box>
                <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: r.ok ? 500 : 600, lineHeight: 1.35 }}>
                  {r.t}
                </Box>
                <Box component="span" sx={{ flexShrink: 0, fontFamily: HEADING, fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: r.ok ? ACC7 : INK }}>
                  {r.meta}
                </Box>
              </Box>
            ))}
            <Box sx={{ p: '11px 14px', fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED58 }}>
              2 itens críticos bloqueiam a abertura do eFRAT
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '22px', flexWrap: 'wrap', mt: '22px', borderTop: '1px solid var(--color-divider)', pt: 2 }}>
            {HERO_STATS.map((s) => (
              <Box key={s.k}>
                <Box sx={{ fontFamily: HEADING, fontSize: 24, fontWeight: 600, lineHeight: 1, letterSpacing: '-0.02em' }}>{s.v}</Box>
                <Box sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED58, mt: '4px' }}>
                  {s.k}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Right: blueprint form card ── */}
        <Box
          className="blueprint"
          sx={{
            width: '100%',
            maxWidth: 420,
            justifySelf: { xs: 'center', md: 'end' },
            p: '26px 26px 22px',
            background: 'transparent',
            boxShadow: 'var(--ind-shadow-md)',
            minWidth: 0,
          }}
        >
          <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />

          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2.5 }}>
            <ZPlateBrand />
          </Box>

          <Typography
            component="h2"
            sx={{ m: 0, fontFamily: HEADING, fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase' }}
          >
            Entrar na operação
          </Typography>
          <Typography sx={{ m: '6px 0 0', fontSize: 13, lineHeight: 1.5, color: MUTED68 }}>
            Use o e-mail da sua operação. Administradores exigem verificação em duas etapas.
          </Typography>

          {infoMessage && (
            <Box sx={{ ...successBoxSx, mt: '14px' }}>{infoMessage}</Box>
          )}

          {error && (
            <Box sx={{ ...errorBoxSx, mt: '14px' }}>
              <Box sx={{ fontWeight: 600 }}>{error}</Box>

              {errorIsAuth && justActivated && (
                // Account-just-created + 401: the most common cause is a
                // claim-invite fallback where the password didn't survive
                // the round-trip. Lead with a one-tap recovery CTA so the
                // user is never stranded.
                <Box sx={{ mt: 1.25 }}>
                  <Typography sx={{ fontSize: 12.5, color: INK, mb: 1.25, lineHeight: 1.45 }}>
                    Acabou de criar sua conta? Se a senha não está sendo aceita,
                    enviamos um novo link de acesso para o seu e-mail.
                  </Typography>
                  <Box
                    component="a"
                    href={buildRecoveryHref()}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 32,
                      px: '13px',
                      border: `1px solid ${INK}`,
                      color: INK,
                      fontFamily: HEADING,
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      textDecoration: 'none',
                    }}
                  >
                    Recuperar minha senha
                  </Box>
                </Box>
              )}

              {errorIsAuth && !justActivated && (
                // Generic recovery hint for any 401 when we DON'T have
                // proof the account was just created — still useful, but
                // less urgent in tone.
                <Typography sx={{ fontSize: 12.5, color: INK, mt: 0.75 }}>
                  Esqueceu a senha?{' '}
                  <Link
                    href={buildRecoveryHref()}
                    underline="hover"
                    sx={{ fontWeight: 700, color: INK }}
                  >
                    Receber link de redefinição
                  </Link>
                  .
                </Typography>
              )}
            </Box>
          )}

          {mfaState ? (
            <form onSubmit={handleMfaSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', mt: '20px' }}>
                <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: MUTED68 }}>
                  {mfaState.enrollmentRequired
                    ? 'Sua conta exige verificação em duas etapas, mas nenhum método está configurado. Entre em contato com o administrador para concluir o cadastro.'
                    : mfaUseRecovery
                      ? 'Digite um dos seus códigos de recuperação.'
                      : 'Digite o código de 6 dígitos do seu aplicativo autenticador.'}
                </Typography>

                {!mfaState.enrollmentRequired && (
                  <>
                    <Box>
                      <Box component="label" htmlFor="login-mfa-code" sx={{ ...labelSx, color: ACC7 }}>
                        {mfaUseRecovery ? 'Código de recuperação' : 'Código de verificação'}
                      </Box>
                      <Box
                        component="input"
                        id="login-mfa-code"
                        className="ind-input"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        required
                        autoFocus
                        autoComplete="one-time-code"
                        inputMode={mfaUseRecovery ? 'text' : 'numeric'}
                        placeholder="000000"
                        style={{
                          ...inputStyle,
                          fontFamily: 'var(--font-heading)',
                          fontSize: 20,
                          fontWeight: 600,
                          letterSpacing: '0.3em',
                          border: `1px solid ${ACC}`,
                        }}
                      />
                    </Box>

                    <Box component="button" type="submit" disabled={loading} sx={primaryBtnSx}>
                      {loading ? 'Verificando...' : 'Verificar'}
                    </Box>

                    {(mfaState.methods || []).includes('webauthn') && (
                      <Box
                        component="button"
                        type="button"
                        disabled={loading}
                        onClick={handlePasskeyVerify}
                        sx={{ ...secondaryBtnSx, width: '100%', minHeight: 40 }}
                      >
                        Usar passkey
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Link
                        component="button"
                        type="button"
                        underline="none"
                        onClick={() => { setMfaUseRecovery((v) => !v); setMfaCode(''); setError(''); }}
                        sx={upLinkSx}
                      >
                        {mfaUseRecovery ? 'Usar código do app' : 'Usar código de recuperação'}
                      </Link>
                      <Link
                        component="button"
                        type="button"
                        underline="none"
                        onClick={cancelMfa}
                        sx={{ ...upLinkSx, color: MUTED58, '&:hover': { color: 'var(--color-text)' } }}
                      >
                        Voltar
                      </Link>
                    </Box>
                  </>
                )}

                {mfaState.enrollmentRequired && (
                  <Box component="button" type="button" onClick={cancelMfa} sx={{ ...secondaryBtnSx, width: '100%', minHeight: 40 }}>
                    Voltar
                  </Box>
                )}
              </Box>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', mt: '20px' }}>
                <Box>
                  <Box component="label" htmlFor="login-username" sx={labelSx}>
                    Usuário ou e-mail
                  </Box>
                  <input
                    id="login-username"
                    className="ind-input"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    autoFocus
                    autoComplete="username"
                    placeholder="nome@empresa.com"
                    style={inputStyle}
                  />
                </Box>

                <Box>
                  <Box component="label" htmlFor="login-password" sx={labelSx}>
                    Senha
                  </Box>
                  <Box sx={{ position: 'relative' }}>
                    <input
                      id="login-password"
                      className="ind-input"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      required
                      autoComplete="current-password"
                      placeholder="••••••••"
                      style={{ ...inputStyle, paddingRight: 44, letterSpacing: '0.08em' }}
                    />
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: MUTED58,
                        borderRadius: 0,
                      }}
                    >
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Link href="/forgot-password" underline="none" sx={{ ...upLinkSx, fontSize: '10.5px', letterSpacing: '0.08em' }}>
                      Esqueceu a senha?
                    </Link>
                  </Box>
                </Box>

                <Box component="button" type="submit" disabled={loading} sx={primaryBtnSx}>
                  {loading ? 'Entrando...' : 'Entrar'}
                </Box>
              </Box>
            </form>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              mt: '20px',
              pt: 2,
              borderTop: '1px dashed var(--color-neutral-300)',
              flexWrap: 'wrap',
            }}
          >
            <Box component="span" sx={{ fontSize: 12.5, color: MUTED68 }}>
              Ainda não é cliente?
            </Box>
            <Link href="/#pricing" underline="none" sx={upLinkSx}>
              Assinar um plano
            </Link>
          </Box>

          <AccessSupportPanel
            sourcePage="login"
            defaultEmail={formData.username.includes('@') ? formData.username : ''}
            title="Precisa de ajuda com login?"
            description="Se a redefinição de senha não chegou, suas credenciais não funcionam ou o link de acesso falhou, fale com o suporte Zulbot."
          />

          {IS_DEV && (
            <Box
              sx={{
                mt: 3,
                p: '10px 12px',
                border: '1px dashed var(--color-neutral-300)',
                background: 'transparent',
              }}
            >
              <Typography sx={{ fontFamily: HEADING, fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: MUTED58, mb: 0.5 }}>
                Demo Credentials:
              </Typography>
              <Typography sx={{ fontSize: 12, color: MUTED68, display: 'block' }}>
                Admin: admin / admin123
              </Typography>
              <Typography sx={{ fontSize: 12, color: MUTED68, display: 'block' }}>
                Pilot: pilot / pilot123
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
